import { selectedDevRunnerIdAtomFamily } from "@/components/ai/chat/state";
import { selectedDevRunnerIdAtom } from "@/components/ai/prompt-input/state";
import { Button } from "@/components/flexkit/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/podkit/dropdown/DropDown";
import { useFlagDevRunnerRegistrationEnabled } from "@/hooks/use-flag";
import { useListDevRunners } from "@/queries/runner-queries";
import { cn } from "@/components/podkit/lib/cn";
import { useAtom } from "jotai";
import { ChevronDown, Server, X } from "lucide-react";
import type { FC } from "react";
import { useMemo } from "react";

export type DevRunnerSelectorProps = {
    /**
     * When provided, uses environment-scoped state (selectedDevRunnerIdAtomFamily).
     * When omitted, uses global state (selectedDevRunnerIdAtom).
     */
    environmentId?: string;
};

/**
 * DevRunnerSelector displays a dropdown to select a dev runner for agent executions.
 * Only visible when the dev_runner_registration_enabled feature flag is enabled.
 */
export const DevRunnerSelector: FC<DevRunnerSelectorProps> = ({ environmentId }) => {
    const { value: isDevRunnerEnabled, loading: isFlagLoading } = useFlagDevRunnerRegistrationEnabled();
    const { devRunners, isLoading: isLoadingRunners } = useListDevRunners(isDevRunnerEnabled);

    // Use environment-scoped atom when environmentId is provided, otherwise use global atom
    const devRunnerAtom = useMemo(
        () => (environmentId ? selectedDevRunnerIdAtomFamily(environmentId) : selectedDevRunnerIdAtom),
        [environmentId],
    );
    const [selectedDevRunnerId, setSelectedDevRunnerId] = useAtom(devRunnerAtom);

    // Don't render if feature flag is disabled or loading
    if (isFlagLoading || !isDevRunnerEnabled) {
        return null;
    }

    // Don't render if no dev runners available
    if (!isLoadingRunners && devRunners.length === 0) {
        return null;
    }

    const selectedRunner = devRunners.find((r) => r.runnerId === selectedDevRunnerId);

    return (
        <div className="flex items-center gap-2">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="secondary"
                        size="sm"
                        className={cn(
                            "max-w-[160px] gap-1.5 text-xs",
                            selectedDevRunnerId && "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400",
                        )}
                        disabled={isLoadingRunners}
                    >
                        <Server className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{selectedRunner ? selectedRunner.name : "Dev Runner"}</span>
                        <ChevronDown className="h-3 w-3 shrink-0" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-w-[280px]">
                    <DropdownMenuItem
                        onClick={() => setSelectedDevRunnerId(null)}
                        className={cn(!selectedDevRunnerId && "bg-pk-surface-secondary")}
                        data-tracking-id="dev-runner-selector-none"
                    >
                        <span className="text-pk-content-secondary">None (use default routing)</span>
                    </DropdownMenuItem>
                    {devRunners.map((runner) => (
                        <DropdownMenuItem
                            key={runner.runnerId}
                            onClick={() => setSelectedDevRunnerId(runner.runnerId)}
                            className={cn(selectedDevRunnerId === runner.runnerId && "bg-pk-surface-secondary")}
                            data-tracking-id="dev-runner-selector-item"
                        >
                            <div className="flex min-w-0 flex-col">
                                <span className="truncate" title={runner.name}>{runner.name}</span>
                                <span className="text-xs text-pk-content-secondary">
                                    {runner.status?.phase}
                                </span>
                            </div>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            {selectedDevRunnerId && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setSelectedDevRunnerId(null)}
                    title="Clear dev runner selection"
                    data-tracking-id="dev-runner-selector-clear"
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            )}
        </div>
    );
};
